import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, X, RefreshCw, Download, MessageCircle, Eye, EyeOff, Inbox } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api } from "../lib/site";
import { ICON_OPTIONS } from "../lib/icons";

const inputCls = "w-full bg-navy-900 border border-glacier/20 rounded-sm px-4 py-2.5 text-sm text-frost placeholder:text-steel/50 focus:outline-none focus:border-ice transition-colors";

const TABS = [
  { key: "leads", label: "Leads Masuk" },
  { key: "settings", label: "Pengaturan Situs" },
  {
    key: "services", label: "Layanan", collection: "services",
    fields: [
      { key: "title", label: "Judul", type: "text", required: true },
      { key: "description", label: "Deskripsi", type: "textarea" },
      { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
      { key: "image", label: "URL Gambar", type: "image" },
      { key: "order", label: "Urutan", type: "number" },
    ],
  },
  {
    key: "products", label: "Produk", collection: "products",
    fields: [
      { key: "name", label: "Nama", type: "text", required: true },
      { key: "description", label: "Deskripsi", type: "textarea" },
      { key: "temperature", label: "Rentang Temperatur", type: "text" },
      { key: "usage", label: "Kegunaan", type: "text" },
      { key: "image", label: "URL Gambar", type: "image" },
      { key: "order", label: "Urutan", type: "number" },
    ],
  },
  {
    key: "advantages", label: "Keunggulan", collection: "advantages",
    fields: [
      { key: "title", label: "Judul", type: "text", required: true },
      { key: "description", label: "Deskripsi", type: "textarea" },
      { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
      { key: "order", label: "Urutan", type: "number" },
    ],
  },
  {
    key: "portfolio", label: "Portofolio", collection: "portfolio",
    fields: [
      { key: "title", label: "Nama Proyek", type: "text", required: true },
      { key: "location", label: "Lokasi", type: "text" },
      { key: "category", label: "Kategori", type: "select", options: ["Cold Storage", "Freezer Room", "Chiller Room", "Blast Freezer", "Refrigeration", "Maintenance"] },
      { key: "type", label: "Jenis Cold Storage", type: "text" },
      { key: "size", label: "Ukuran", type: "text" },
      { key: "temperature", label: "Temperatur", type: "text" },
      { key: "year", label: "Tahun", type: "text" },
      { key: "image", label: "URL Foto", type: "image" },
      { key: "description", label: "Deskripsi", type: "textarea" },
    ],
  },
  {
    key: "testimonials", label: "Testimoni", collection: "testimonials",
    fields: [
      { key: "name", label: "Nama", type: "text", required: true },
      { key: "company", label: "Perusahaan", type: "text" },
      { key: "project", label: "Jenis Proyek", type: "text" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "text", label: "Testimoni", type: "textarea" },
      { key: "avatar", label: "URL Foto/Avatar", type: "image" },
    ],
  },
  {
    key: "faqs", label: "FAQ", collection: "faqs",
    fields: [
      { key: "question", label: "Pertanyaan", type: "text", required: true },
      { key: "answer", label: "Jawaban", type: "textarea" },
      { key: "order", label: "Urutan", type: "number" },
    ],
  },
  {
    key: "stats", label: "Statistik", collection: "stats",
    fields: [
      { key: "label", label: "Label", type: "text", required: true },
      { key: "value", label: "Angka", type: "number" },
      { key: "suffix", label: "Suffix (mis. +)", type: "text" },
      { key: "order", label: "Urutan", type: "number" },
    ],
  },
];

const SETTINGS_FIELDS = [
  { key: "brand_name", label: "Nama Brand (logo teks)" },
  { key: "company_name", label: "Nama Perusahaan (legal)" },
  { key: "tagline", label: "Tagline" },
  { key: "description", label: "Deskripsi singkat perusahaan", type: "textarea" },
  { key: "wa_number", label: "Nomor WhatsApp (format 628xxx)" },
  { key: "wa_display", label: "Nomor WhatsApp (tampilan)" },
  { key: "wa_message", label: "Pesan otomatis WhatsApp", type: "textarea" },
  { key: "email", label: "Email" },
  { key: "address", label: "Alamat" },
  { key: "hours", label: "Jam Operasional" },
  { key: "maps_url", label: "URL Embed Google Maps (opsional)" },
  { key: "hero_image", label: "URL Foto Hero", type: "image" },
  { key: "about_image", label: "URL Foto Tentang Kami", type: "image" },
  { key: "instagram", label: "URL Instagram (opsional)" },
  { key: "facebook", label: "URL Facebook (opsional)" },
  { key: "linkedin", label: "URL LinkedIn (opsional)" },
];

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("admin_token", data.token);
      toast.success("Login berhasil");
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-950 blueprint-grid flex items-center justify-center px-5" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-sm bg-navy-900 border border-glacier/25 rounded-sm p-8"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <span className="bg-white rounded-sm px-2 py-1.5 flex items-center">
            <img src="/logo-mark.png" alt="Logo SolusiCold" className="h-6 w-auto" />
          </span>
          <span className="font-display text-xl font-bold uppercase text-frost">Panel Admin</span>
        </div>
        <p className="text-xs text-steel mb-7">Masuk untuk mengelola konten website.</p>
        <div className="space-y-4">
          <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
          <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
          <button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full bg-ice text-navy-950 font-semibold py-3 rounded-sm hover:bg-glacier transition-colors disabled:opacity-60">
            {loading ? "Memproses..." : "Masuk"}
          </button>
        </div>
        <a href="/" className="block text-center text-xs text-steel hover:text-ice mt-6 transition-colors" data-testid="admin-back-home">← Kembali ke website</a>
      </motion.form>
    </div>
  );
}

function CrudTab({ tab }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/content");
    setItems(data[tab.collection] || []);
    setLoading(false);
  }, [tab.collection]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const blank = {};
    tab.fields.forEach((f) => (blank[f.key] = f.type === "number" ? 0 : ""));
    setForm(blank);
    setEditing("new");
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post(`/admin/${tab.collection}`, form);
        toast.success("Item ditambahkan");
      } else {
        await api.put(`/admin/${tab.collection}/${editing}`, form);
        toast.success("Perubahan disimpan");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Gagal menyimpan");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    await api.delete(`/admin/${tab.collection}/${id}`);
    toast.success("Item dihapus");
    load();
  };

  const titleKey = tab.fields[0].key;

  return (
    <div data-testid={`admin-tab-${tab.key}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold uppercase text-frost">{tab.label}</h2>
        <button data-testid={`admin-add-${tab.key}`} onClick={openNew} className="flex items-center gap-2 bg-ice text-navy-950 text-sm font-semibold px-4 py-2.5 rounded-sm hover:bg-glacier transition-colors">
          <Plus className="w-4 h-4" /> Tambah
        </button>
      </div>

      {loading ? (
        <p className="text-steel text-sm">Memuat...</p>
      ) : items.length === 0 ? (
        <p className="text-steel text-sm border border-dashed border-glacier/25 rounded-sm p-8 text-center">Belum ada data. Klik "Tambah" untuk membuat.</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((item, i) => (
            <div key={item.id} data-testid={`admin-item-${tab.key}-${i}`} className="flex items-center gap-4 bg-navy-900 border border-glacier/15 rounded-sm px-5 py-4">
              {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded-sm object-cover shrink-0" loading="lazy" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-frost truncate">{item[titleKey]}</p>
                {(item.description || item.answer || item.text) && (
                  <p className="text-xs text-steel truncate">{(item.description || item.answer || item.text || "").slice(0, 90)}</p>
                )}
              </div>
              <button data-testid={`admin-edit-${tab.key}-${i}`} onClick={() => openEdit(item)} className="w-9 h-9 flex items-center justify-center text-steel hover:text-ice transition-colors" aria-label="Edit">
                <Pencil className="w-4 h-4" />
              </button>
              <button data-testid={`admin-delete-${tab.key}-${i}`} onClick={() => remove(item.id)} className="w-9 h-9 flex items-center justify-center text-steel hover:text-red-400 transition-colors" aria-label="Hapus">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-navy-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              className="bg-navy-900 border border-glacier/25 rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto p-7"
              data-testid={`admin-form-${tab.key}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-xl font-bold uppercase text-frost">{editing === "new" ? "Tambah" : "Edit"} {tab.label}</h3>
                <button type="button" onClick={() => setEditing(null)} className="text-steel hover:text-frost" aria-label="Tutup"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                {tab.fields.map((f) => (
                  <div key={f.key}>
                    <label className="block font-mono text-[10px] tracking-[0.2em] text-steel uppercase mb-1.5">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea data-testid={`admin-field-${f.key}`} rows={3} required={f.required} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
                    ) : f.type === "select" ? (
                      <select data-testid={`admin-field-${f.key}`} required={f.required} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls}>
                        <option value="">Pilih...</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        data-testid={`admin-field-${f.key}`}
                        type={f.type === "number" ? "number" : "text"}
                        required={f.required}
                        value={form[f.key] ?? ""}
                        onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                        className={inputCls}
                      />
                    )}
                    {f.type === "image" && form[f.key] && <img src={form[f.key]} alt="preview" className="mt-2 h-20 rounded-sm object-cover" />}
                  </div>
                ))}
              </div>
              <button data-testid={`admin-save-${tab.key}`} type="submit" className="mt-7 w-full bg-ice text-navy-950 font-semibold py-3 rounded-sm hover:bg-glacier transition-colors">
                Simpan
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setForm(data.settings || {}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Pengaturan disimpan. Refresh website untuk melihat perubahan.");
    } catch {
      toast.error("Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="admin-tab-settings" className="max-w-2xl">
      <h2 className="font-display text-2xl font-bold uppercase text-frost mb-6">Pengaturan Situs</h2>
      <div className="space-y-4">
        {SETTINGS_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block font-mono text-[10px] tracking-[0.2em] text-steel uppercase mb-1.5">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`settings-field-${f.key}`} rows={2} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            ) : (
              <input data-testid={`settings-field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            )}
            {f.type === "image" && form[f.key] && <img src={form[f.key]} alt="preview" className="mt-2 h-20 rounded-sm object-cover" />}
          </div>
        ))}
      </div>
      <button data-testid="settings-save-btn" type="submit" disabled={saving} className="mt-7 bg-ice text-navy-950 font-semibold px-8 py-3 rounded-sm hover:bg-glacier transition-colors disabled:opacity-60">
        {saving ? "Menyimpan..." : "Simpan Pengaturan"}
      </button>
    </form>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRead = async (lead) => {
    await api.patch(`/admin/leads/${lead.id}`, { read: !lead.read });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus lead ini?")) return;
    await api.delete(`/admin/leads/${id}`);
    load();
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(leads, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };

  return (
    <div data-testid="admin-tab-leads">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl font-bold uppercase text-frost">Leads Masuk ({leads.length})</h2>
        <div className="flex gap-2.5">
          <button data-testid="leads-refresh-btn" onClick={load} className="flex items-center gap-2 border border-glacier/30 text-steel text-sm px-4 py-2.5 rounded-sm hover:text-ice hover:border-ice transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button data-testid="leads-export-btn" onClick={exportJson} className="flex items-center gap-2 border border-glacier/30 text-steel text-sm px-4 py-2.5 rounded-sm hover:text-ice hover:border-ice transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-steel text-sm">Memuat...</p>
      ) : leads.length === 0 ? (
        <div className="text-center border border-dashed border-glacier/25 rounded-sm p-12" data-testid="leads-empty">
          <Inbox className="w-8 h-8 text-steel/50 mx-auto mb-3" />
          <p className="text-steel text-sm">Belum ada leads masuk. Leads dari form penawaran & kontak akan tampil di sini.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map((l, i) => (
            <div key={l.id} data-testid={`lead-card-${i}`} className={`bg-navy-900 border rounded-sm p-5 ${l.read ? "border-glacier/10 opacity-70" : "border-ice/40"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2.5">
                    <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm ${l.type === "quotation" ? "bg-ice/10 text-ice" : "bg-glacier/10 text-glacier"}`}>
                      {l.type === "quotation" ? "Penawaran" : "Kontak"}
                    </span>
                    {!l.read && <span className="w-2 h-2 rounded-full bg-ice" title="Baru" />}
                    <span className="text-[11px] text-steel/60">{l.created_at ? new Date(l.created_at).toLocaleString("id-ID") : ""}</span>
                  </div>
                  <p className="mt-2.5 text-sm font-semibold text-frost">{l.name} {l.company ? `— ${l.company}` : ""}</p>
                  <p className="text-xs text-steel mt-0.5">
                    {[l.business, l.storage_type, (l.length || l.width || l.height) && `${l.length || "-"}x${l.width || "-"}x${l.height || "-"} m`, l.temperature, l.location].filter(Boolean).join(" • ")}
                  </p>
                  {l.message && <p className="text-xs text-steel mt-1.5 italic">"{l.message}"</p>}
                </div>
                <div className="flex gap-2">
                  {l.wa && (
                    <a data-testid={`lead-wa-${i}`} href={`https://wa.me/${String(l.wa).replace(/\D/g, "").replace(/^0/, "62")}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-glacier/25 rounded-sm text-[#25D366] hover:border-[#25D366] transition-colors" aria-label="Chat WA">
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                  <button data-testid={`lead-read-${i}`} onClick={() => toggleRead(l)} className="w-9 h-9 flex items-center justify-center border border-glacier/25 rounded-sm text-steel hover:text-ice transition-colors" aria-label="Tandai">
                    {l.read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button data-testid={`lead-delete-${i}`} onClick={() => remove(l.id)} className="w-9 h-9 flex items-center justify-center border border-glacier/25 rounded-sm text-steel hover:text-red-400 transition-colors" aria-label="Hapus">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("leads");

  const check = useCallback(async () => {
    try {
      await api.get("/auth/me");
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-navy-950 flex items-center justify-center text-steel text-sm">Memeriksa sesi...</div>;
  }
  if (!authed) {
    return (<><LoginView onSuccess={() => setAuthed(true)} /><Toaster theme="dark" position="top-center" richColors /></>);
  }

  return (
    <div className="min-h-screen bg-navy-950 text-frost" data-testid="admin-panel">
      <header className="border-b border-glacier/15 bg-navy-900">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="bg-white rounded-sm px-1.5 py-1 flex items-center">
              <img src="/logo-mark.png" alt="Logo SolusiCold" className="h-5 w-auto" />
            </span>
            <span className="font-display text-lg font-bold uppercase">Panel Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-steel hover:text-ice transition-colors" data-testid="admin-view-site">Lihat Website</a>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-1.5 text-xs text-steel hover:text-red-400 transition-colors">
              <LogOut className="w-4 h-4" /> Keluar
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-btn-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap text-xs font-semibold px-4 py-2.5 rounded-sm border transition-colors ${
                tab === t.key ? "bg-ice text-navy-950 border-ice" : "border-glacier/20 text-steel hover:text-ice hover:border-ice/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab />}
        {tab === "settings" && <SettingsTab />}
        {TABS.filter((t) => t.collection).map((t) => tab === t.key && <CrudTab key={t.key} tab={t} />)}
      </div>
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
